<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisField from 'dashboard/components-next/LaisField.vue';
import LaisSelect from 'dashboard/components-next/LaisSelect.vue';
import { WHATSAPP_PROVIDER_OPTIONS, input } from '../utils/minhaLaisOptions';
import {
  formatDateTime,
  whatsappOnboardingStatusClass,
  whatsappOnboardingStatusLabel,
} from '../utils/minhaLaisPresenters';

defineProps({
  whatsappChecklistItems: { type: Array, default: () => [] },
  whatsappChecklistProgress: { type: Object, required: true },
  isLoadingWhatsappGovernance: { type: Boolean, default: false },
  isSavingWhatsappOnboarding: { type: Boolean, default: false },
});

const emit = defineEmits(['save']);

const whatsappGovernance = defineModel({
  type: Object,
  required: true,
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <LaisCard title="Onboarding Meta">
    <div
      v-if="isLoadingWhatsappGovernance"
      class="flex items-center gap-2 rounded-lais border border-[#e5e7eb] bg-[#faf8fd] p-4 text-[13px] text-[#6b7280]"
    >
      <span class="i-lucide-loader-circle size-4 animate-spin" />
      Carregando onboarding...
    </div>

    <div v-else class="grid gap-5">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p class="text-[13px] font-semibold text-lais-text">
            Pré-requisitos obrigatórios:
            {{ whatsappChecklistProgress.completed }}/{{
              whatsappChecklistProgress.total
            }}
          </p>
          <p class="mt-1 text-[12px] text-[#6b7280]">
            Termos Meta:
            {{
              whatsappGovernance.meta_terms_accepted ? 'aceitos' : 'pendentes'
            }}
          </p>
        </div>
        <span
          class="inline-flex items-center rounded-full border px-3 py-1 text-[12px] font-semibold"
          :class="
            whatsappOnboardingStatusClass(whatsappGovernance.onboarding_status)
          "
        >
          {{
            whatsappOnboardingStatusLabel(whatsappGovernance.onboarding_status)
          }}
        </span>
      </div>

      <div class="grid gap-4 lg:grid-cols-2">
        <LaisField label="Provider de onboarding">
          <LaisSelect
            v-model="whatsappGovernance.metadata.provider"
            :options="WHATSAPP_PROVIDER_OPTIONS"
          />
        </LaisField>

        <LaisField label="Responsável Business Manager">
          <input
            v-model="whatsappGovernance.metadata.business_manager_owner"
            type="text"
            :class="input"
          />
        </LaisField>

        <LaisField label="Portfólio/conta Meta">
          <input
            v-model="whatsappGovernance.metadata.meta_portfolio_url"
            type="url"
            :class="input"
          />
        </LaisField>

        <label
          class="flex min-h-[44px] items-center gap-3 rounded-lais border border-[#e5e7eb] bg-white px-3 py-2 text-[13px] font-semibold text-lais-text"
        >
          <input
            v-model="whatsappGovernance.metadata.meta_ads_connected"
            type="checkbox"
            class="size-4 shrink-0 accent-lais-primary"
          />
          Meta Ads conectado
        </label>
      </div>

      <div class="grid gap-3">
        <article
          v-for="item in whatsappChecklistItems"
          :key="item.key"
          class="rounded-lais border border-[#e5e7eb] bg-white p-4"
        >
          <label class="flex items-start gap-3">
            <input
              v-model="whatsappGovernance.checklist[item.key].checked"
              type="checkbox"
              class="mt-0.5 size-4 shrink-0 accent-lais-primary"
            />
            <span class="min-w-0">
              <span class="block text-[13px] font-semibold text-lais-text">
                {{ item.label }}
                <span
                  v-if="item.required"
                  class="ml-1 text-[11px] font-semibold text-[#b42318]"
                >
                  obrigatório
                </span>
              </span>
            </span>
          </label>

          <div
            v-if="item.requires_evidence || item.requires_responsible"
            class="mt-3 grid gap-3 md:grid-cols-2"
          >
            <LaisField v-if="item.requires_evidence" label="Evidência">
              <input
                v-model="whatsappGovernance.checklist[item.key].evidence"
                type="text"
                :class="input"
              />
            </LaisField>
            <LaisField v-if="item.requires_responsible" label="Responsável">
              <input
                v-model="whatsappGovernance.checklist[item.key].responsible"
                type="text"
                :class="input"
              />
            </LaisField>
          </div>
        </article>
      </div>

      <div class="rounded-lais border border-[#e5e7eb] bg-[#fcfbfd] p-4">
        <label
          class="flex items-start gap-3 text-[13px] font-semibold text-lais-text"
        >
          <input
            v-model="whatsappGovernance.meta_terms_accepted"
            type="checkbox"
            class="mt-0.5 size-4 shrink-0 accent-lais-primary"
          />
          Termos da API Meta aceitos
        </label>
        <LaisField label="Evidência dos termos" class="mt-3">
          <textarea
            v-model="whatsappGovernance.metadata.meta_terms_evidence"
            rows="3"
            :class="input"
          />
        </LaisField>
        <p class="mt-2 text-[12px] text-[#6b7280]">
          Aceito por:
          {{ whatsappGovernance.meta_terms_accepted_by?.name || 'aguardando' }}
          <span v-if="whatsappGovernance.meta_terms_accepted_at">
            ·
            {{ formatDateTime(whatsappGovernance.meta_terms_accepted_at) }}
          </span>
        </p>
      </div>

      <button
        type="button"
        class="inline-flex h-11 w-fit items-center justify-center gap-2 rounded-lais bg-lais-primary px-4 text-[14px] font-semibold text-white transition-colors hover:bg-[#3a2762] disabled:cursor-not-allowed disabled:opacity-60"
        :disabled="isSavingWhatsappOnboarding"
        @click="emit('save')"
      >
        <span
          class="size-4"
          :class="
            isSavingWhatsappOnboarding
              ? 'i-lucide-loader-circle animate-spin'
              : 'i-lucide-save'
          "
        />
        Salvar onboarding
      </button>
    </div>
  </LaisCard>
</template>
